import MuiTableRow from '@mui/material/TableRow';
import type { KeyboardEvent, ReactNode } from 'react';

export interface TableRowProps {
  onClick?: () => void;
  selected?: boolean;
  hover?: boolean;
  children: ReactNode;
}

// Table row behind the facade. A clickable row carries the keyboard affordances a
// bare <tr onClick> lacks: it becomes a focusable button that activates on Enter and
// Space (Space is swallowed so the page does not scroll), so agents who never touch
// the mouse can still open a delivery. `selected` reflects "open as a sub-tab", which
// the shell may set on several rows at once.
//
// `hover` tints the row under the pointer. A clickable row always gets it (an affordance
// it cannot go without), so the prop only speaks for read-only rows: opt-in, because on a
// table with nothing to click the tint is a reading aid — it keeps the eye on one line
// across wide columns — and a row that lights up without being actionable can read as
// clickable, so the consumer decides.
export function TableRow({ onClick, selected = false, hover = false, children }: TableRowProps) {
  if (!onClick) {
    return (
      <MuiTableRow hover={hover} selected={selected}>
        {children}
      </MuiTableRow>
    );
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLTableRowElement>): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick();
    }
  };

  return (
    <MuiTableRow
      hover
      selected={selected}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
    >
      {children}
    </MuiTableRow>
  );
}
